import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import staffInvitationService from '../../services/staffInvitationService';
import CSRFToken from '../../security/CSRFToken';
import { sanitizeText, validateEmail } from '../../security/sanitize';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';

const StaffModal = ({ isOpen, onClose, onSave, staff = null, availablePermissions, brandId }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    designation: '',
    status: 'active',
    permissions: {}
  });
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('basic');
  const [expandedModules, setExpandedModules] = useState({});
  const [inviteStatus, setInviteStatus] = useState({
    sent: false,
    sending: false,
    error: null
  });

  // Initialize form data when editing an existing staff member
  useEffect(() => {
    if (staff) {
      setFormData({
        name: staff.name || '',
        email: staff.email || '',
        designation: staff.designation || '',
        status: staff.status || 'active',
        permissions: staff.permissions || {}
      });
      
      // Pre-expand modules with assigned permissions
      const modulesWithPermissions = {};
      Object.keys(staff.permissions || {}).forEach(moduleId => {
        if (staff.permissions[moduleId]?.length > 0) {
          modulesWithPermissions[moduleId] = true;
        }
      });
      setExpandedModules(modulesWithPermissions);
    } else {
      // Reset form when adding a new staff member
      setFormData({
        name: '',
        email: '',
        designation: '',
        status: 'active',
        permissions: {}
      });
      setExpandedModules({});
    }
  }, [staff]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handlePermissionChange = (moduleId, permissionId, checked) => {
    setFormData(prev => {
      const updatedPermissions = { ...prev.permissions };
      
      if (!updatedPermissions[moduleId]) {
        updatedPermissions[moduleId] = [];
      }
      
      if (checked) {
        // Add permission if it doesn't exist
        if (!updatedPermissions[moduleId].includes(permissionId)) {
          updatedPermissions[moduleId] = [...updatedPermissions[moduleId], permissionId];
        }
      } else {
        // Remove permission
        updatedPermissions[moduleId] = updatedPermissions[moduleId].filter(id => id !== permissionId);
        
        // Remove empty module arrays
        if (updatedPermissions[moduleId].length === 0) {
          delete updatedPermissions[moduleId];
        }
      }
      
      return {
        ...prev,
        permissions: updatedPermissions
      };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.designation.trim()) {
      newErrors.designation = 'Designation is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate CSRF token
    const csrfToken = document.querySelector('input[name="csrfToken"]')?.value;
    if (!csrfToken || csrfToken !== sessionStorage.getItem('csrfToken')) {
      setErrors(prev => ({
        ...prev,
        form: 'Security validation failed. Please refresh the page and try again.'
      }));
      return;
    }
    
    if (validateForm()) {
      try {
        // Sanitize form data before saving
        const sanitizedFormData = {
          name: sanitizeText(formData.name),
          email: validateEmail(formData.email),
          designation: sanitizeText(formData.designation),
          status: formData.status,
          permissions: formData.permissions // Permissions object structure is controlled by the UI
        };
        
        // Save the staff member data first
        const savedStaffId = await onSave(sanitizedFormData);
        
        // If this is a new staff member (not an edit), send an invitation
        if (!staff && savedStaffId) {
          try {
            setInviteStatus({
              sent: false,
              sending: true,
              error: null
            });
            
            const inviteResult = await staffInvitationService.inviteStaffMember(
              brandId, 
              savedStaffId, 
              sanitizedFormData.email
            );
            
            setInviteStatus({
              sent: true,
              sending: false,
              error: null
            });
          } catch (error) {
            console.error('Error sending invitation:', error);
            setInviteStatus({
              sent: false,
              sending: false,
              error: error.message || 'Failed to send invitation'
            });
          }
        } else {
          // Close modal immediately for edits
          onClose();
        }
      } catch (error) {
        console.error('Error saving staff data:', error);
        // Handle the error appropriately
      }
    }  
  };

  const handleResendInvitation = async () => {
    if (!staff || !staff.email) return;
    
    // Validate CSRF token
    const csrfToken = document.querySelector('input[name="csrfToken"]')?.value;
    if (!csrfToken || csrfToken !== sessionStorage.getItem('csrfToken')) {
      setInviteStatus({
        ...inviteStatus,
        error: 'Security validation failed. Please refresh the page and try again.'
      });
      return;
    }
    
    try {
      setInviteStatus({
        ...inviteStatus,
        sending: true,
        error: null
      });
      
      // Sanitize email before sending
      const sanitizedEmail = validateEmail(staff.email);
      const result = await staffInvitationService.resendInvitation(sanitizedEmail);
      
      setInviteStatus({
        sent: result.success,
        sending: false,
        error: result.success ? null : result.message
      });
      
    } catch (error) {
      console.error('Error resending invitation:', error);
      setInviteStatus({
        ...inviteStatus,
        sending: false,
        error: 'Failed to resend invitation email.'
      });
    }
  };

  const isPermissionChecked = (moduleId, permissionId) => {
    return formData.permissions[moduleId]?.includes(permissionId) || false;
  };

  const toggleModuleExpansion = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={onClose}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <X className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                    {staff ? 'Edit Staff Member' : 'Add Staff Member'}
                  </Dialog.Title>
                  
                  {/* Tab Navigation */}
                  <div className="border-b border-gray-200 mt-4">
                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                      <button
                        type="button"
                        onClick={() => setActiveTab('basic')}
                        className={`${
                          activeTab === 'basic'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Basic Info
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveTab('permissions')}
                        className={`${
                          activeTab === 'permissions'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Permissions
                        {Object.keys(formData.permissions).length > 0 && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary text-white">
                            {Object.keys(formData.permissions).length}
                          </span>
                        )}
                      </button>
                    </nav>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="mt-4">
                    <CSRFToken />
                    {errors.form && (
                      <div className="mb-4 p-2 bg-red-50 border border-red-300 text-red-700 rounded">
                        {errors.form}
                      </div>
                    )}
                    {/* Basic Info Tab */}
                    {activeTab === 'basic' && (
                      <div className="space-y-4">
                        {/* Name Field */}
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                            Name
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`mt-1 block w-full border ${errors.name ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                          />
                          {errors.name && (
                            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                          )}
                        </div>
                        
                        {/* Email Field */}
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                            Email
                          </label>
                          <input
                            type="email"
                            name="email"
                            id="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={`mt-1 block w-full border ${errors.email ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                          />
                          {errors.email && (
                            <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                          )}
                        </div>
                        
                        {/* Designation Field */}
                        <div>
                          <label htmlFor="designation" className="block text-sm font-medium text-gray-700">
                            Designation
                          </label>
                          <input
                            type="text"
                            name="designation"
                            id="designation"
                            value={formData.designation}
                            onChange={handleChange}
                            className={`mt-1 block w-full border ${errors.designation ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                            placeholder="e.g. Marketing Manager"
                          />
                          {errors.designation && (
                            <p className="mt-1 text-sm text-red-600">{errors.designation}</p>
                          )}
                        </div>
                        
                        {/* Status Field */}
                        <div>
                          <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                            Status
                          </label>
                          <Select
                            value={formData.status}
                            onValueChange={(value) => handleChange({ target: { name: 'status', value } })}
                          >
                            <SelectTrigger id="status" className="w-full">
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="inactive">Inactive</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                    
                    {/* Permissions Tab */}
                    {activeTab === 'permissions' && (
                      <div className="mt-2">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="text-sm font-medium text-gray-900">Staff Permissions</h4>
                          <div className="text-xs text-gray-500">
                            {Object.keys(formData.permissions).length} module(s) with permissions
                          </div>
                        </div>
                        
                        {/* Quick actions */}
                        <div className="mb-3 flex space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              // Expand all modules
                              const allModules = {};
                              availablePermissions.forEach(module => {
                                allModules[module.module] = true;
                              });
                              setExpandedModules(allModules);
                            }}
                            className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-700"
                          >
                            Expand All
                          </button>
                          <button
                            type="button"
                            onClick={() => setExpandedModules({})}
                            className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-700"
                          >
                            Collapse All
                          </button>
                        </div>
                        
                        <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                          {availablePermissions.map((module) => {
                            // Check if this module has any permissions set
                            const hasPermissions = formData.permissions[module.module]?.length > 0;
                            
                            return (
                              <div 
                                key={module.module} 
                                className={`border ${hasPermissions ? 'border-primary bg-blue-50' : 'border-gray-200'} rounded-md transition-colors duration-200`}
                              >
                                {/* Collapsible Header */}
                                <button
                                  type="button"
                                  onClick={() => toggleModuleExpansion(module.module)}
                                  className="w-full flex justify-between items-center p-3 text-left focus:outline-none"
                                >
                                  <div className="flex items-center">
                                    <h5 className="text-sm font-medium text-gray-700">{module.name}</h5>
                                    {hasPermissions && (
                                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary text-white">
                                        {formData.permissions[module.module]?.length || 0}
                                      </span>
                                    )}
                                  </div>
                                  {expandedModules[module.module] ? (
                                    <ChevronUp className="h-5 w-5 text-gray-500" />
                                  ) : (
                                    <ChevronDown className="h-5 w-5 text-gray-500" />
                                  )}
                                </button>
                                
                                {/* Collapsible Content */}
                                {expandedModules[module.module] && (
                                  <div className="p-3 pt-0 border-t border-gray-200">
                                    <div className="space-y-2">
                                      {module.permissions.map((permission) => {
                                        const isChecked = isPermissionChecked(module.module, permission.id);
                                        return (
                                          <div key={permission.id} className="flex items-start hover:bg-gray-50 p-1 rounded transition-colors">
                                            <div className="flex items-center h-5">
                                              <input
                                                id={permission.id}
                                                name={permission.id}
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={(e) => handlePermissionChange(module.module, permission.id, e.target.checked)}
                                                className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                                              />
                                            </div>
                                            <div className="ml-3 text-sm">
                                              <label 
                                                htmlFor={permission.id} 
                                                className={`font-medium ${isChecked ? 'text-primary' : 'text-gray-700'}`}
                                              >
                                                {permission.name}
                                              </label>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Invitation Status Message */}
                    {inviteStatus.sending && (
                      <div className="mt-3 p-3 bg-blue-50 text-blue-700 rounded-md flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending invitation email...
                      </div>
                    )}
                    
                    {inviteStatus.sent && (
                      <div className="mt-3 p-3 bg-green-50 text-green-700 rounded-md flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Invitation email sent successfully!
                      </div>
                    )}
                    
                    {inviteStatus.error && (
                      <div className="mt-3 p-3 bg-red-50 text-red-700 rounded-md flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        {inviteStatus.error}
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                      <button
                        type="submit"
                        disabled={inviteStatus.sending}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {staff ? 'Save Changes' : 'Add Staff & Send Invitation'}
                      </button>
                      <button
                        type="button"
                        disabled={inviteStatus.sending}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:col-start-1 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={onClose}
                      >
                        Cancel
                      </button>
                    </div>
                    
                    {/* Resend Invitation Button (only for existing staff) */}
                    {staff && staff.email && (
                      <div className="mt-3 text-center">
                        <button
                          type="button"
                          onClick={handleResendInvitation}
                          disabled={inviteStatus.sending}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Resend Invitation
                        </button>
                      </div>
                    )}
                    
                    {/* Tab navigation buttons at bottom */}
                    <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between">
                      {activeTab === 'permissions' && (
                        <button
                          type="button"
                          onClick={() => setActiveTab('basic')}
                          className="text-sm text-primary hover:text-primary-dark flex items-center"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                          Back to Basic Info
                        </button>
                      )}
                      {activeTab === 'basic' && (
                        <button
                          type="button"
                          onClick={() => setActiveTab('permissions')}
                          className="text-sm text-primary hover:text-primary-dark flex items-center ml-auto"
                        >
                          Continue to Permissions
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default StaffModal;
